import {
  addCalendarDaysToKey,
  addCalendarMonthsToKey,
  asCalendarDate,
  asCompanyLocalDateTime,
  asUtcInstant,
  asWallTime,
  calendarDateAtInstant,
  calendarDayDifference,
  calendarDateKeyFromParts,
  calendarDateKeyFromWallDate,
  companyLocalDateTimeToInstant,
  companyPickerToUtcInstant,
  dateKeyInDisplayTimezone,
  formatTimezoneLabel,
  getDisplayDateFormat,
  getDisplayDateTimeFormat,
  getDisplayLocale,
  getDisplayTimeFormat,
  getDisplayTimezone,
  parseStoredUtc,
  relativeDayGroup,
  resetDisplaySettings,
  timezoneLabel,
  todayInDisplayTimezone,
  toUtcIsoStringFromCompanyLocal,
  utcIsoToCompanyLocalPicker,
  utcInstantToCompanyPicker,
} from '@/utils/displayContext';
import type {
  CalendarDate,
  CompanyLocalDateTime,
  RelativeDayGroup,
  UtcInstantIso,
  WallTime,
} from '@/utils/displayContext';
import {
  formatDateTimeWithPreset,
  formatDateWithPreset,
  formatPickerLocalDateTimeWithPreset,
  formatTimeWithPreset,
} from '@/utils/displayFormatEngine';

export function formatCurrency(
  value: number | string | null | undefined,
  currency = 'INR',
): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat(getDisplayLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

export function formatNumber(value: number | string | null | undefined, decimals = 2): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat(getDisplayLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

export function formatInteger(value: number | string | null | undefined): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat(getDisplayLocale(), {
    maximumFractionDigits: 0,
  }).format(num);
}

export function formatPercent(value: number | string | null | undefined, decimals = 2): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat(getDisplayLocale(), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

export function formatPhoneNumber(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return '';
  const normalized = raw.replace(/[^\d+]/g, '');
  if (!normalized.startsWith('+')) {
    return raw;
  }
  const digits = normalized.slice(1);
  const country = digits.slice(0, digits.length > 10 ? digits.length - 10 : Math.min(2, digits.length));
  const national = digits.slice(country.length);
  if (!country || !national) {
    return raw;
  }
  return `+${country} ${national.replace(/(\d{3})(?=\d)/g, '$1 ').trim()}`;
}

export interface AddressParts {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export function formatAddress(parts: AddressParts): string {
  const compact = (items: Array<string | null | undefined>) => items.map((item) => item?.trim()).filter(Boolean);
  const locale = getDisplayLocale().toLowerCase();
  const cityLine = locale.startsWith('fr')
    ? compact([parts.postalCode, parts.city]).join(' ')
    : compact([parts.city, parts.state, parts.postalCode]).join(', ');
  return compact([parts.line1, parts.line2, cityLine, parts.country]).join('\n');
}

export function formatDate(value: string | null | undefined): string {
  return formatDateWithPreset(value, getDisplayDateFormat(), getDisplayTimezone(), getDisplayLocale());
}

/** Format company-local picker datetime (`yyyy-MM-dd'T'HH:mm`) for display labels. */
export function formatPickerLocalDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return formatPickerLocalDateTimeWithPreset(
    value,
    getDisplayDateTimeFormat(),
    getDisplayDateFormat(),
    getDisplayTimeFormat(),
    getDisplayLocale(),
  );
}

export function formatDateTime(value: string | null | undefined, options?: { showTimezone?: boolean }): string {
  const formatted = formatDateTimeWithPreset(
    value,
    getDisplayDateTimeFormat(),
    getDisplayDateFormat(),
    getDisplayTimeFormat(),
    getDisplayTimezone(),
    getDisplayLocale(),
  );
  if (options?.showTimezone && value) {
    const date = parseStoredUtc(value);
    if (!Number.isNaN(date.getTime())) {
      return `${formatted} ${formatTimezoneLabel(date)}`;
    }
  }
  return formatted;
}

export function formatTime(value: string | null | undefined): string {
  return formatTimeWithPreset(value, getDisplayTimeFormat(), getDisplayTimezone(), getDisplayLocale());
}

/** Format UTC ISO value for display in the active company timezone (alias for tables). */
export function formatDisplayDateTime(value: string | null | undefined): string {
  return formatDateTime(value, { showTimezone: true });
}

export function formatDisplayDate(value: string | null | undefined): string {
  return formatDate(value);
}

/** Convert a local Date picker value to UTC ISO string for API payloads. */
export function toUtcIsoString(value: Date): string {
  return value.toISOString();
}

/** Convert company-local datetime string from pickers to UTC ISO for API payloads. */
export function toUtcIsoStringFromPicker(localValue: string): string {
  return toUtcIsoStringFromCompanyLocal(localValue);
}

/** Today's calendar date in company/display timezone (yyyy-MM-dd). */
export function todayInCompanyTimezone(): string {
  return todayInDisplayTimezone();
}

export function currentCompanyYear(): number {
  return Number(todayInDisplayTimezone().slice(0, 4));
}

export function currentCompanyMonth(): number {
  return Number(todayInDisplayTimezone().slice(5, 7));
}

export {
  utcIsoToCompanyLocalPicker,
  utcInstantToCompanyPicker,
  companyPickerToUtcInstant,
  companyLocalDateTimeToInstant,
  calendarDateAtInstant,
  calendarDayDifference,
  calendarDateKeyFromParts,
  calendarDateKeyFromWallDate,
  addCalendarDaysToKey,
  addCalendarMonthsToKey,
  dateKeyInDisplayTimezone,
  relativeDayGroup,
  timezoneLabel,
  asUtcInstant,
  asCalendarDate,
  asWallTime,
  asCompanyLocalDateTime,
  parseStoredUtc,
  resetDisplaySettings,
};

export type {
  UtcInstantIso,
  CalendarDate,
  WallTime,
  CompanyLocalDateTime,
  RelativeDayGroup,
};

export {
  DISPLAY_DATE_FORMAT_OPTIONS,
  DISPLAY_DATETIME_FORMAT_OPTIONS,
  DISPLAY_TIME_FORMAT_OPTIONS,
  displayFormatExampleLabel,
} from '@/utils/displayFormatConstants';
export { previewDisplayFormats } from '@/utils/displayFormatEngine';
export type {
  DisplayDateFormatId,
  DisplayDateTimeFormatId,
  DisplayTimeFormatId,
} from '@/utils/displayFormatConstants';
